import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, CheckCircle, FileText, TrendingUp, Users, Award, Clock, Zap } from 'lucide-react';
import { standardServicePricing } from '../../pricingData';
import { Button } from '../../components/ui/button';
import { PricingSection } from '../../components/PricingSection';
import { TestimonialSlider } from '../../components/TestimonialSlider';
import { ProcessSlider } from '../../components/ProcessSlider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { faqs } from '../../mock';
import { useScrollAnimation, useCounterAnimation } from '../../hooks/useScrollAnimation';

const StatCard = ({ icon: Icon, value, label, delay }) => {
  const [ref, count] = useCounterAnimation(parseInt(value), 2000);
  return (
    <div ref={ref} className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all" style={{ animationDelay: `${delay}ms` }}>
      <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon className="text-orange-600" size={32} />
      </div>
      <div className="text-4xl font-bold text-gray-900 mb-2">{count}+</div>
      <div className="text-gray-600">{label}</div>
    </div>
  );
};

export const TrademarkApplicationPreparation = ({ onOpenPopup }) => {
  const [benefitsRef, benefitsVisible] = useScrollAnimation(0.2);
  const [statsRef, statsVisible] = useScrollAnimation(0.2);

  return (
    <div className="pt-20">
      <Helmet>
        <title>Trademark Application Preparation | Professional Legal Services</title>
        <meta name="description" content="Expert trademark application preparation services for seamless filing" />
      </Helmet>

      <section className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-32 left-1/4 animate-float"><Shield className="w-20 h-20 text-white/10" /></div>
          <div className="absolute bottom-32 right-1/3 animate-float" style={{ animationDelay: '1s' }}><FileText className="w-24 h-24 text-white/10" /></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6 animate-slide-in">
              <span className="text-sm font-semibold">⭐ Professional Service</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-black mb-6 leading-tight animate-slide-in">
              Trademark Application Preparation
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90 animate-slide-in leading-relaxed">
              Professional preparation of trademark applications for successful USPTO filing
            </p>
            <div className="flex flex-wrap gap-4 justify-center animate-slide-in">
              <Button onClick={onOpenPopup} size="lg" className="bg-white text-orange-600 hover:bg-gray-100 text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all hover:scale-105">
                Get Started Today
              </Button>
              <Button onClick={onOpenPopup} variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 backdrop-blur-sm">
                Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section ref={benefitsRef} className={`py-20 bg-gray-50 ${benefitsVisible ? 'animate-fade-in' : 'opacity-0'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">Why Choose Our Application Preparation?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Expert preparation ensures your application meets all USPTO requirements</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-2">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <FileText className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Complete Documentation</h3>
              <p className="text-gray-600 leading-relaxed">Comprehensive preparation of all required forms and supporting documents</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-2">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Shield className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Expert Review</h3>
              <p className="text-gray-600 leading-relaxed">Thorough review by experienced trademark attorneys before submission</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-2">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Accuracy Guarantee</h3>
              <p className="text-gray-600 leading-relaxed">Meticulous attention to detail to minimize rejection risks</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-2">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Clock className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Fast Turnaround</h3>
              <p className="text-gray-600 leading-relaxed">Quick preparation without compromising on quality</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-2">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Zap className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Strategic Filing</h3>
              <p className="text-gray-600 leading-relaxed">Optimal filing strategy based on your business needs</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-2">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Award className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Full Support</h3>
              <p className="text-gray-600 leading-relaxed">Ongoing guidance throughout the entire application process</p>
            </div>
          </div>
        </div>
      </section>

      <ProcessSlider />

      <section ref={statsRef} className={`py-20 bg-white ${statsVisible ? 'animate-fade-in' : 'opacity-0'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">Our Track Record</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Proven success in trademark application preparation</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <StatCard icon={Users} value="2500" label="Applications Prepared" delay={0} />
            <StatCard icon={CheckCircle} value="98" label="Success Rate" delay={100} />
            <StatCard icon={Award} value="15" label="Years Experience" delay={200} />
            <StatCard icon={TrendingUp} value="50" label="States Covered" delay={300} />
          </div>
        </div>
      </section>

      <PricingSection plans={standardServicePricing} onSelectPlan={onOpenPopup} />

      <TestimonialSlider />

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-black text-center mb-12 text-gray-900">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.slice(0, 6).map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-lg shadow-md px-6">
                  <AccordionTrigger className="text-left text-lg font-semibold hover:text-orange-600">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6">Ready to File Your Trademark Application?</h2>
          <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
            Let our experts prepare your trademark application for a smooth filing process
          </p>
          <Button onClick={onOpenPopup} size="lg" className="bg-white text-orange-600 hover:bg-gray-100 text-lg px-12 py-6 shadow-xl hover:shadow-2xl transition-all hover:scale-105">
            Start Your Application
          </Button>
        </div>
      </section>
    </div>
  );
};
